import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Globe,
  ShoppingCart,
  Brain,
  ArrowUpRight,
  Lock,
  Package,
  X,
  Users,
  Gamepad2,
  Bot,
  ExternalLink,
  Github,
  Star,
  Layers,
} from "lucide-react";
import { useTranslation } from "react-i18next";

const projectData = [
  {
    id: "jatjeng",
    title: "JatJeng Inventory Management System",
    type: "SaaS Application",
    descKey: "projects.items.jatjeng",
    tech: ["React", "TypeScript", "Tailwind CSS", "REST API"],
    isPrivate: false,
    isFeatured: true,
    icon: Package,
    link: "https://jatjeng.site",
    statusKey: "projects.status.live",
    team: [
      {
        name: "Penhchey",
        roleKey: "projects.roles.frontend",
        image: "https://api.dicebear.com/7.x/avataaars/svg?seed=Felix&backgroundColor=b6e3f4",
      },
      {
        name: "Rangsey",
        roleKey: "projects.roles.frontend",
        image: "https://api.dicebear.com/7.x/avataaars/svg?seed=RangSey&backgroundColor=c0aede",
      },
      {
        name: "Rith",
        roleKey: "projects.roles.backend",
        image: "https://api.dicebear.com/7.x/avataaars/svg?seed=Rith&backgroundColor=ffdfbf",
      },
    ],
  },
  {
    id: "aiNovel",
    title: "AI Novel Translation Platform",
    type: "Full-Stack Web App",
    descKey: "projects.items.aiNovel",
    tech: ["React", "TypeScript", "Laravel", "MySQL"],
    isPrivate: true,
    isFeatured: true,
    icon: Globe,
    statusKey: "projects.status.inProgress",
  },
  {
    id: "restaurant",
    title: "Restaurant Ordering & Admin System",
    type: "Web Application",
    descKey: "projects.items.restaurant",
    tech: ["Laravel", "Blade", "MySQL", "PHP"],
    isPrivate: false,
    isFeatured: true,
    icon: ShoppingCart,
    github: "https://github.com/pechpenhchey/PP-Y4.git",
    statusKey: "projects.status.completed",
    team: [
      {
        name: "Penhchey",
        roleKey: "projects.roles.dev",
        image: "https://api.dicebear.com/7.x/avataaars/svg?seed=Felix&backgroundColor=b6e3f4",
      },
      {
        name: "Rangsey",
        roleKey: "projects.roles.dev",
        image: "https://api.dicebear.com/7.x/avataaars/svg?seed=RangSey&backgroundColor=c0aede",
      },
    ],
  },
  {
    id: "attrition",
    title: "Employee Attrition Prediction",
    type: "Data Science & ML",
    descKey: "projects.items.attrition",
    tech: ["Python", "Random Forest", "Pandas", "Scikit-Learn"],
    isPrivate: false,
    icon: Brain,
    github: "https://github.com/Rangsey0/Employee_Attrition_Predict.git",
    statusKey: "projects.status.completed",
  },
  {
    id: "shooter",
    title: "3D Action Shooting Game",
    type: "Game Development",
    descKey: "projects.items.shooter",
    tech: ["Unity 3D", "C#", "Physics Engine"],
    isPrivate: false,
    icon: Gamepad2,
    github: "https://github.com/Rangsey0/GAD-3D-Shooter.git",
    statusKey: "projects.status.completed",
  },
  {
    id: "discord",
    title: "Discord Utility & Moderation Bot",
    type: "Automation & Bot",
    descKey: "projects.items.discord",
    tech: ["Python", "discord.py", "REST APIs"],
    isPrivate: false,
    icon: Bot,
    github: "https://github.com/Rangsey0/discord-bot.git",
    statusKey: "projects.status.completed",
  },
];

export default function Projects() {
  const { t } = useTranslation();
  const [selectedProject, setSelectedProject] = useState(null);
  const [filter, setFilter] = useState("all");

  // Close modal on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setSelectedProject(null);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const filteredProjects =
    filter === "featured"
      ? projectData.filter((p) => p.isFeatured)
      : projectData;

  return (
    <section
      id="projects"
      className="py-20 px-6 sm:px-12 lg:px-20 bg-neutral-50/60 dark:bg-black text-neutral-900 dark:text-white relative border-t border-neutral-200/60 dark:border-neutral-900"
    >
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <span className="inline-block py-1 px-3 rounded-full bg-neutral-200/80 dark:bg-neutral-900 text-neutral-800 dark:text-neutral-300 text-xs font-semibold tracking-wider uppercase mb-3 border border-neutral-300/80 dark:border-neutral-800">
            {t("projects.badge")}
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight mb-4 text-neutral-900 dark:text-white">
            {t("projects.title")}
          </h2>
          <p className="text-neutral-600 dark:text-neutral-400 max-w-2xl mx-auto text-base sm:text-lg">
            {t("projects.desc")}
          </p>
        </motion.div>

        {/* Filter Pills */}
        <div className="flex items-center justify-center gap-2 mb-10">
          <button
            onClick={() => setFilter("all")}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200 ${
              filter === "all"
                ? "bg-neutral-950 text-white dark:bg-white dark:text-neutral-950 shadow-sm"
                : "bg-white text-neutral-600 dark:bg-neutral-900/60 dark:text-neutral-400 border border-neutral-200/80 dark:border-neutral-800 hover:text-neutral-900 dark:hover:text-white"
            }`}
          >
            <Layers size={14} />
            <span>{t("projects.filterAll")}</span>
          </button>
          <button
            onClick={() => setFilter("featured")}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200 ${
              filter === "featured"
                ? "bg-neutral-950 text-white dark:bg-white dark:text-neutral-950 shadow-sm"
                : "bg-white text-neutral-600 dark:bg-neutral-900/60 dark:text-neutral-400 border border-neutral-200/80 dark:border-neutral-800 hover:text-neutral-900 dark:hover:text-white"
            }`}
          >
            <Star size={14} className={filter === "featured" ? "fill-current" : ""} />
            <span>{t("projects.filterFeatured")}</span>
          </button>
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project) => {
            const Icon = project.icon;

            return (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
                className={`group relative flex flex-col justify-between rounded-2xl p-6 transition-all duration-300
                  bg-white dark:bg-neutral-950/70 
                  border ${
                    project.isFeatured
                      ? "border-neutral-300 dark:border-neutral-700/80 shadow-sm hover:border-neutral-400 dark:hover:border-neutral-600"
                      : "border-neutral-200/80 dark:border-neutral-800/80 hover:border-neutral-300 dark:hover:border-neutral-700"
                  } hover:shadow-lg dark:hover:shadow-black/40`}
              >
                <div>
                  {/* Top Bar: Icon, Category & Featured Badge */}
                  <div className="flex items-center justify-between mb-5">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-neutral-100 dark:bg-neutral-900 border border-neutral-200/70 dark:border-neutral-800 text-neutral-800 dark:text-neutral-200 group-hover:scale-105 transition-transform duration-200">
                      <Icon size={20} className="text-blue-500" />
                    </div>

                    <div className="flex items-center gap-2">
                      {project.isFeatured && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20">
                          <Star size={10} className="fill-current" />
                          <span>{t("projects.featuredBadge")}</span>
                        </span>
                      )}
                      <span className="text-[11px] font-medium text-neutral-500 dark:text-neutral-400">
                        {project.type}
                      </span>
                    </div>
                  </div>

                  {/* Title */}
                  <h3
                    onClick={() => setSelectedProject(project)}
                    className="text-lg font-bold text-neutral-900 dark:text-white cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors mb-2.5 flex items-center justify-between group-hover:translate-x-0.5 duration-200"
                  >
                    <span>{project.title}</span>
                  </h3>

                  {/* Description */}
                  <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed line-clamp-3 mb-6">
                    {t(project.descKey)}
                  </p>
                </div>

                <div>
                  {/* Tech stack tags */}
                  <div className="flex flex-wrap gap-1.5 mb-5">
                    {project.tech.map((tech, idx) => (
                      <span
                        key={idx}
                        className="px-2.5 py-1 rounded-lg text-xs font-medium bg-neutral-100 dark:bg-neutral-900 text-neutral-700 dark:text-neutral-300 border border-neutral-200/60 dark:border-neutral-800/80"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>

                  {/* Card Action Row */}
                  <div className="pt-4 border-t border-neutral-100 dark:border-neutral-900 flex items-center justify-between text-xs font-semibold">
                    <button
                      onClick={() => setSelectedProject(project)}
                      className="text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white transition-colors"
                    >
                      {t("projects.details")}
                    </button>

                    <div className="flex items-center gap-3">
                      {project.link && (
                        <a
                          href={project.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          <span>{t("projects.liveDemo")}</span>
                          <ExternalLink size={12} />
                        </a>
                      )}

                      {project.github && (
                        <a
                          href={project.github}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-neutral-700 dark:text-neutral-300 hover:text-neutral-950 dark:hover:text-white transition-colors"
                        >
                          <Github size={13} />
                          <span>{t("projects.viewCode")}</span>
                        </a>
                      )}

                      {project.isPrivate && (
                        <span className="inline-flex items-center gap-1 text-neutral-400 dark:text-neutral-500">
                          <Lock size={12} />
                          <span>{t("projects.private")}</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Project Details Modal */}
      <AnimatePresence>
        {selectedProject && (
          <div
            role="dialog"
            aria-modal="true"
            className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6"
          >
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setSelectedProject(null)}
              className="absolute inset-0 bg-neutral-950/60 dark:bg-black/80 backdrop-blur-sm"
            />

            {/* Modal Box */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="relative w-full max-w-2xl bg-white dark:bg-neutral-950 rounded-2xl shadow-2xl border border-neutral-200 dark:border-neutral-800 overflow-hidden z-10 max-h-[90vh] flex flex-col"
            >
              {/* Header */}
              <div className="p-6 sm:p-8 border-b border-neutral-200/80 dark:border-neutral-800/80 flex items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-neutral-100 dark:bg-neutral-900 border border-neutral-200/80 dark:border-neutral-800 shrink-0">
                    <selectedProject.icon size={22} className="text-blue-500" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-semibold text-neutral-500 dark:text-neutral-400">
                        {selectedProject.type}
                      </span>
                      {selectedProject.statusKey && (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-neutral-100 dark:bg-neutral-900 text-neutral-700 dark:text-neutral-300 border border-neutral-200 dark:border-neutral-800">
                          {t(selectedProject.statusKey)}
                        </span>
                      )}
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-white">
                      {selectedProject.title}
                    </h3>
                  </div>
                </div>

                <button
                  onClick={() => setSelectedProject(null)}
                  aria-label="Close dialog"
                  className="p-2 rounded-xl text-neutral-400 hover:text-neutral-900 dark:hover:text-white hover:bg-neutral-100 dark:hover:bg-neutral-900 transition-colors"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="p-6 sm:p-8 overflow-y-auto space-y-6">
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-2">
                    About Project
                  </h4>
                  <p className="text-sm sm:text-base text-neutral-700 dark:text-neutral-300 leading-relaxed">
                    {t(selectedProject.descKey)}
                  </p>
                </div>

                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-2.5">
                    {t("projects.tech")}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedProject.tech.map((tech, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1.5 rounded-xl text-xs font-semibold bg-neutral-100 dark:bg-neutral-900 text-neutral-800 dark:text-neutral-200 border border-neutral-200/80 dark:border-neutral-800"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                {selectedProject.team && selectedProject.team.length > 0 && (
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3 flex items-center gap-1.5">
                      <Users size={14} />
                      <span>{t("projects.team")}</span>
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {selectedProject.team.map((member, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-3 p-3 rounded-xl bg-neutral-50 dark:bg-neutral-900/60 border border-neutral-200/70 dark:border-neutral-800/80"
                        >
                          <img
                            src={member.image}
                            alt={member.name}
                            className="w-9 h-9 rounded-full bg-white dark:bg-black border border-neutral-200 dark:border-neutral-700"
                          />
                          <div className="min-w-0">
                            <div className="text-xs font-bold text-neutral-900 dark:text-white truncate">
                              {member.name}
                            </div>
                            <div className="text-[10px] text-neutral-500 dark:text-neutral-400 truncate">
                              {t(member.roleKey)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer Actions */}
              <div className="p-6 border-t border-neutral-200/80 dark:border-neutral-800/80 bg-neutral-50/50 dark:bg-neutral-950 flex flex-wrap items-center justify-end gap-3">
                {selectedProject.link && (
                  <a
                    href={selectedProject.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs sm:text-sm font-semibold bg-neutral-950 text-white dark:bg-white dark:text-neutral-950 hover:bg-neutral-800 dark:hover:bg-neutral-100 transition-colors shadow-sm"
                  >
                    <span>{t("projects.liveDemo")}</span>
                    <ArrowUpRight size={14} />
                  </a>
                )}

                {selectedProject.github && (
                  <a
                    href={selectedProject.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-neutral-800 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                  >
                    <Github size={15} />
                    <span>{t("projects.viewCode")}</span>
                  </a>
                )}

                {selectedProject.isPrivate && (
                  <span className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-semibold text-neutral-500 bg-neutral-100 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800">
                    <Lock size={13} />
                    <span>{t("projects.private")}</span>
                  </span>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </section>
  );
}
